import React, {useEffect, useState} from "react";
import Button from 'Components/DesignSystem/Button/Button';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import Typography from 'Components/DesignSystem/Typography/Typography';
import {UIModelAPI} from "../../types/api.types";
import {fetchUIModelsApi} from "../../services/ApiHelper";
import { toast } from 'Components/DesignSystem/Toast/useToast';

type PromotionUIModelProps = {
    id?: string;
    projectId: string
    projectName: string;
    onClose: () => void;
    onSuccess: (ids: string[]) => void;
}

const PromotionUIModel = ({id, projectId, projectName, onClose, onSuccess}: PromotionUIModelProps) => {
    const baseId = id || 'promotion-ui-model';

    const [loading, setLoading] = useState(false);
    const [models, setModels] = useState<UIModelAPI[]>([]);
    const [selectedModel, setSelectedModel] = useState<string>('');
    const [useOldUIModel, setUseOldUIModel] = useState<boolean>(false);

    useEffect(() => {
        fetchModels()
    }, []);

    const fetchModels = async () => {
        setLoading(true);
        try {
            const responseData = await fetchUIModelsApi(projectId, {pageNumber: 1, pageSize: 100}, 'name', 'asc', '');
            if (responseData?.done) {
                setModels(responseData.data.models);
                const promotedModel = responseData.data.models.find((model: any) => model.is_promoted);
                if(promotedModel) {
                    setSelectedModel(promotedModel.id);
                }
            }
        } catch (error) {
            // Handle error
        } finally {
            setLoading(false);
        }
    }

    // The DS Dropdown reports the chosen value itself (the old <select> handed over its change event).
    const handleModelChange = (value: string | number) => {
        setSelectedModel(String(value));
    };

    // The DS Checkbox reports `checked` itself (the old <input> handed over its change event).
    const handleCheckboxChange = (isChecked: boolean) => {
        setUseOldUIModel(isChecked);
        
        // Clear selected model when checkbox is checked
        if (isChecked) {
            setSelectedModel('');
        }
    };

    const handlePromote = async () => {
        // Validate: Either a model must be selected OR old UI model checkbox must be checked
        if (!useOldUIModel && !selectedModel) {
            toast.warning("Either UI model or UI definition required for promotion");
            return;
        }

        // If checkbox is checked, send empty array to use old UI model
        // Otherwise, send selected model ID
        if (useOldUIModel) {
            onSuccess([]);
        } else {
            onSuccess(selectedModel.length > 0 ? [selectedModel] : []);
        }
    };

    // Presentation only (2026-09-09): the component now renders its own DS Dialog — the legacy
    // PopUp wrapper that MainPageHeader used to put around it is gone — and every control is a
    // design-system component bound to the SAME state and handlers as before. What gets
    // promoted is still decided above (handlePromote → onSuccess with the chosen ids).
    const footer = (
        <div id={`${baseId}-actions`} className='tw-flex tw-w-full tw-gap-2 tw-justify-end'>
            <Button
                id={`${baseId}-cancel-btn`}
                data-testid={`${baseId}-cancel-btn`}
                variant="secondary"
                onClick={onClose}
            >
                Cancel
            </Button>
            <Button
                id={`${baseId}-promote-btn`}
                data-testid={`${baseId}-promote-btn`}
                variant="primary"
                disabled={loading}
                onClick={handlePromote}
            >
                {loading ? 'Please wait...' : 'Promote'}
            </Button>
        </div>
    );

    return (
        <Dialog
            isOpen
            onClose={onClose}
            title="Promote"
            size="md"
            data-testid={`${baseId}-dialog`}
            footer={footer}
        >
            <div id={baseId} data-testid={baseId} className='tw-space-y-5 tw-text-ink'>
                <div id={`${baseId}-model-section`} className='tw-space-y-1'>
                    <Typography id={`${baseId}-model-label`} data-testid={`${baseId}-model-label`} variant="sub-title-label" as="p" className="tw-text-ink-secondary">
                        Model
                    </Typography>
                    <Typography id={`${baseId}-model-name`} variant="heading-3" as="h3" className="tw-text-ink">
                        {projectName}
                    </Typography>
                </div>

                <div id={`${baseId}-ui-model-section`} className="tw-space-y-3">
                    <Typography id={`${baseId}-ui-model-label`} data-testid={`${baseId}-ui-model-label`} variant="sub-title-label" as="p" className="tw-text-ink-secondary">
                        UI Model
                    </Typography>

                    {loading ? (
                        <div
                            id={`${baseId}-ui-model-loading`}
                            data-testid={`${baseId}-ui-model-loading`}
                            className="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-border tw-border-edge tw-rounded-ctl"
                        >
                            <Spinner size="sm" label="Loading UI models" />
                            <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">Loading models...</Typography>
                        </div>
                    ) : (
                        <Dropdown
                            label=""
                            aria-label="UI Model"
                            data-testid={`${baseId}-ui-model-select`}
                            options={models.map((model) => ({ value: model.id, label: model.name }))}
                            value={selectedModel}
                            onChange={handleModelChange}
                            placeholder={models.length === 0 ? 'No UI models available' : 'Select UI model'}
                            disabled={models.length === 0 || useOldUIModel}
                            usePortal
                            className="tw-w-full"
                        />
                    )}

                    <Checkbox
                        label="Use SB 2.x UI (deprecated)"
                        checked={useOldUIModel}
                        onChange={handleCheckboxChange}
                        disabled={loading}
                        data-testid={`${baseId}-old-ui-model-input`}
                    />
                </div>
            </div>
        </Dialog>
    );
}

export default PromotionUIModel;